<template>
  <SogCard v-show="_loading || _items.length > 0">
    <SogCardTitle>Vorschläge für die Verknüpfung</SogCardTitle>
    <SogCardContent v-show="!_loading">
      <SogFlexColumn>
        <div class="hint">
          Die erfasste Nummer passt zu keiner Police. Folgende Policen könnten gemeint sein – bitte prüfen und übernehmen.
        </div>
        <table class="suggestions">
          <thead>
            <tr>
              <th>Erfasst</th>
              <th>Vorschlag Police</th>
              <th>Abweichung</th>
              <th>Sicherheit</th>
              <th>Police erstellt</th>
              <th />
            </tr>
          </thead>
          <tbody
            v-for="item in _items"
            :key="item.id"
          >
            <tr>
              <td>
                <span
                  v-for="(seg, i) in recordedSegments(item)"
                  :key="i"
                  :class="{diff: seg.diff}"
                >{{ seg.text }}</span>
              </td>
              <td>
                <a
                  class="policy-link"
                  href="#"
                  title="Police in der Vorschau anzeigen"
                  @click.prevent="onShowInsurance(item)"
                >
                  <span
                    v-for="(seg, i) in suggestedSegments(item)"
                    :key="i"
                    :class="{diff: seg.diff}"
                  >{{ seg.text }}</span>
                </a>
              </td>
              <td>{{ matchTypeDescription(item.matchType) }}</td>
              <td>
                <span :class="['confidence', 'confidence-' + item.confidence]">{{ item.confidenceText }}</span>
              </td>
              <td>{{ formatDate(item.insuranceCreateDate) }}</td>
              <td class="actions">
                <SogCardActionButton
                  mdi-icon="mdi-check"
                  tooltip="Vorschlag übernehmen"
                  @click="onAccept(item)"
                />
                <SogCardActionButton
                  mdi-icon="mdi-close"
                  tooltip="Vorschlag ablehnen"
                  @click="onReject(item)"
                />
              </td>
            </tr>
            <tr
              v-if="item.neighborhoodText"
              class="info"
            >
              <td colspan="6">
                {{ item.neighborhoodText }}
              </td>
            </tr>
          </tbody>
        </table>
      </SogFlexColumn>
    </SogCardContent>
    <SogCardContent>
      <SogSkeleton v-show="_loading" />
    </SogCardContent>
    <SogCardActions v-show="!_loading && _items.length > 1">
      <SogCardActionButton
        mdi-icon="mdi-close-circle-multiple"
        tooltip="Alle Vorschläge ablehnen"
        @click="onRejectAll"
      />
    </SogCardActions>
  </SogCard>
</template>

<script setup lang="ts">
import {ref, watch} from "vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogFlexColumn from "@/shared/ui/SogFlexColumn.vue";
import SogSkeleton from "@/shared/ui/SogSkeleton.vue";
import {useNavigationStore} from "@/modules/Navigation/store";
import apiVehicleRegistration from "@/modules/VehicleRegistration/api";
import notificationService from "@/shared/services/NotificationService";
import VehicleRegistrationSuggestion, {matchTypeDescription} from "@/shared/model/VehicleRegistrationSuggestion";
import {diffSegments, normalizeNumber, TextSegment} from "@/modules/VehicleRegistration/utils/suggestionDiff";

interface Props {
  vehicleRegistrationId?: string
}

const props = withDefaults(defineProps<Props>(), {vehicleRegistrationId: undefined})

const _emit = defineEmits<{ (e: 'changed'): void }>()

const _navigationStore = useNavigationStore()
const _items = ref<VehicleRegistrationSuggestion[]>([])
const _loading = ref(false)

async function load() {
  _items.value = []
  if (!props.vehicleRegistrationId) {
    return
  }
  _loading.value = true
  try {
    _items.value = (await apiVehicleRegistration.suggestions(props.vehicleRegistrationId)) ?? []
  } finally {
    _loading.value = false
  }
}

watch(() => props.vehicleRegistrationId, load, {immediate: true})

/** Erfasste Nummer, die zu diesem Vorschlag geführt hat (Versicherungsschein-Nr. oder eVB-Nr.). */
function recordedNumber(item: VehicleRegistrationSuggestion): string {
  const t_candidates = item.matchType === "EvbTypo"
    ? [item.versicherungsBestaetigungsNummer]
    : [item.versicherungsScheinnummer, item.versicherungsBestaetigungsNummer]
  const t_target = normalizeNumber(item.insuranceNumber)
  let t_best = ""
  let t_bestDistance = Number.MAX_SAFE_INTEGER
  for (const t_c of t_candidates) {
    const t_norm = normalizeNumber(t_c)
    if (!t_norm) continue
    const t_d = Math.abs(t_norm.length - t_target.length) + [...t_norm].filter((c, i) => c !== t_target[i]).length
    if (t_d < t_bestDistance) {
      t_best = t_c ?? ""
      t_bestDistance = t_d
    }
  }
  return t_best
}

function recordedSegments(item: VehicleRegistrationSuggestion): TextSegment[] {
  return diffSegments(recordedNumber(item), item.insuranceNumber ?? "").recorded
}

function suggestedSegments(item: VehicleRegistrationSuggestion): TextSegment[] {
  return diffSegments(recordedNumber(item), item.insuranceNumber ?? "").suggested
}

function formatDate(value?: Date | string): string {
  if (!value) return ""
  const t_date = new Date(value)
  return isNaN(t_date.getTime()) ? "" : t_date.toLocaleDateString("de-DE")
}

/** Zeigt die vorgeschlagene Police in der Versicherungskarte (Seitenleiste) an. */
function onShowInsurance(item: VehicleRegistrationSuggestion) {
  if (item.insuranceId) {
    _navigationStore.currentInsuranceId = item.insuranceId
  }
}

function onAccept(item: VehicleRegistrationSuggestion) {
  if (!item.id) return
  notificationService.showYesNo(
    "Vorschlag übernehmen",
    "Soll die Zulassung mit der Police " + item.insuranceNumber + " verknüpft werden?",
    async () => {
      const t_res = await apiVehicleRegistration.acceptSuggestion(item.id ?? "")
      if (t_res) {
        notificationService.success("Zulassung wurde mit der Police " + item.insuranceNumber + " verknüpft")
      }
      // auch bei Fehler neu laden: der Vorschlag kann inzwischen veraltet sein
      await load()
      _emit("changed")
    })
}

async function onReject(item: VehicleRegistrationSuggestion) {
  if (!item.id) return
  const t_res = await apiVehicleRegistration.rejectSuggestion(item.id, undefined)
  if (t_res) {
    notificationService.success("Vorschlag wurde abgelehnt")
    await load()
    _emit("changed")
  }
}

async function onRejectAll() {
  if (!props.vehicleRegistrationId) return
  const t_res = await apiVehicleRegistration.rejectSuggestion(undefined, props.vehicleRegistrationId)
  if (t_res) {
    notificationService.success("Alle Vorschläge wurden abgelehnt")
    await load()
    _emit("changed")
  }
}

defineExpose({load})
</script>

<style scoped>
.hint {
  margin-bottom: 8px;
}

.suggestions {
  border-collapse: collapse;
}

.suggestions th,
.suggestions td {
  padding: 4px 12px 4px 0;
  text-align: left;
  vertical-align: middle;
}

.suggestions td {
  font-family: monospace;
}

.suggestions td:nth-child(3),
.suggestions td:nth-child(4),
.suggestions td:nth-child(5) {
  font-family: inherit;
}

.confidence {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 0.85em;
  white-space: nowrap;
  color: #fff;
  background: #757575;
}

.confidence-A {
  background: #2e7d32;
}

.confidence-B {
  background: #689f38;
}

.confidence-C {
  background: #f9a825;
  color: #000;
}

.confidence-D {
  background: #d32f2f;
}

.diff {
  color: #d32f2f;
  font-weight: bold;
  text-decoration: underline;
}

.policy-link {
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px dotted currentColor;
}

.policy-link:hover {
  border-bottom-style: solid;
}

.actions {
  white-space: nowrap;
}

.suggestions tr.info td {
  font-family: inherit;
  font-size: 0.85em;
  color: #757575;
  padding-top: 0;
  padding-bottom: 10px;
}
</style>
